import * as THREE from 'three';
import { flattenWaypoints, poseAt } from '@/lib/path';
import { elementBox } from '@/render/batch';
import type { LayerRig } from '@/render/rig';
import { stations } from '@/state/tour';
import { STORE_LAYER } from '@/hero/StoreLayer';
import type { Frame } from '../types';

// An original element the edit removed: it only exists left of the cut.
export interface Removed {
  rig: LayerRig;
  expressID: number;
  center: THREE.Vector3;
  shown: boolean | null;
}

// Where the cut stands this frame, for the sheet the scene draws along it.
export const cut = {
  active: false,
  point: new THREE.Vector3(),
  normal: new THREE.Vector3(1, 0, 0),
  width: 0,
  height: 0,
};

const MARGIN = 0.5;
const path = flattenWaypoints(stations);
const box = new THREE.Box3();
const plane = new THREE.Plane();
const up = new THREE.Vector3(0, 1, 0);
const forward = new THREE.Vector3();
const side = new THREE.Vector3();
const v = new THREE.Vector3();

// World box of everything drawn, original and edited, so the wipe spans the whole building.
export function measure(rigs: LayerRig[]): void {
  box.makeEmpty();
  const el = new THREE.Box3();
  for (const rig of rigs)
    for (const entry of rig.elements.values()) box.union(elementBox(entry, el));
}

// A vertical plane through the hero, square to the tour camera, moved by the slider. The overlay
// keeps what lies right of it; removed originals keep what lies left of it.
export function swipeTo(frame: Frame, swipe: number, removed: Removed[]): void {
  if (box.isEmpty()) {
    cut.active = false;
    return;
  }
  const pose = poseAt(path, frame.progress);
  forward.set(...pose.lookAt).sub(v.set(...pose.position));
  cut.normal.crossVectors(forward, up).setY(0);
  if (cut.normal.lengthSq() < 1e-6) cut.normal.set(1, 0, 0);
  cut.normal.normalize();
  side.crossVectors(up, cut.normal);

  let lo = Infinity;
  let hi = -Infinity;
  let near = Infinity;
  let far = -Infinity;
  for (let i = 0; i < 8; i++) {
    v.set(
      i & 1 ? box.max.x : box.min.x,
      i & 2 ? box.max.y : box.min.y,
      i & 4 ? box.max.z : box.min.z,
    );
    const d = cut.normal.dot(v);
    const s = side.dot(v);
    lo = Math.min(lo, d);
    hi = Math.max(hi, d);
    near = Math.min(near, s);
    far = Math.max(far, s);
  }
  const at = lo - MARGIN + (hi - lo + 2 * MARGIN) * swipe;

  plane.normal.copy(cut.normal);
  plane.constant = -at;
  frame.layers.get(STORE_LAYER)?.rig.setClipPlane(plane);
  for (const r of removed) {
    const shown = cut.normal.dot(r.center) < at;
    if (shown === r.shown) continue;
    r.rig.setElementVisible(r.expressID, shown);
    r.shown = shown;
  }

  box.getCenter(v);
  cut.point.copy(v).addScaledVector(cut.normal, at - cut.normal.dot(v));
  cut.width = far - near + 2 * MARGIN;
  cut.height = box.max.y - box.min.y + 2 * MARGIN;
  cut.active = true;
}

export function clearSwipe(): void {
  cut.active = false;
  box.makeEmpty();
}
